import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Loading from '../../../common/components/indicators/loading/Loading';
import NotAccessible from '../../../common/components/layout/not-accessible/NotAccessible';
import { useCheckOrderBookAccess } from '../../../common/util/check-access/useCheckOrderBookAccess';
import {
  useOfferingSidePanelMatch,
  useOfferingSidePanelOfferingId,
} from '../hooks/useOfferingSidePanelMatch';
import offeringSidePanelSellSideRouteFactory from '../offeringSidePanelSellSideRouteFactory';
import ComplianceRoute from './compliance/ComplianceRoute';
import IndicationActivitySellSideRoute from './indication-activity/IndicationActivitySellSideRoute';
import UpdateHistoryRoute from './update-history/UpdateHistoryRoute';

/**
 * Side Panel routes for the Sell Side sections of the
 * Order Book module
 */
const OrderBookSellSideRoute: React.FC = () => {
  const offeringId = useOfferingSidePanelOfferingId();
  const { canRead, loading: accessLoading } = useCheckOrderBookAccess({
    offeringId,
  });

  const sidePanelMatch = useOfferingSidePanelMatch();
  const rest = sidePanelMatch?.rest || '/';

  if (accessLoading) {
    return <Loading />;
  }

  if (!canRead) {
    return <NotAccessible subject="Order Book" />;
  }

  // `rest` isn't a real router location (see useOfferingSidePanelMatch.ts) — `<Routes
  // location={rest}>` matches against it explicitly instead of the actual browser location.
  //
  // This component is itself rendered as the element for OfferingSidePanelSellSideRoutes' own
  // `${orderBook.routePath}/*` <Route>, so react-router has already established "order-book" as
  // the ambient pathnameBase by the time we get here. Even though `rest` above still needs to
  // include that "order-book" prefix (react-router's explicit-location <Routes> requires the
  // given location to start with the ambient pathnameBase), react-router strips that same prefix
  // back off before matching it against the routes below — so those routes must be relative to
  // "order-book", not repeat it, or every match here falls through to the "*" catch-all instead.
  return (
    <Routes location={rest}>
      <Route
        path={`${offeringSidePanelSellSideRouteFactory.orderBookIndicationActivity.routePath.replace(
          offeringSidePanelSellSideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<IndicationActivitySellSideRoute />}
      />
      <Route
        path={`${offeringSidePanelSellSideRouteFactory.orderBookUpdateHistory.routePath.replace(
          offeringSidePanelSellSideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<UpdateHistoryRoute />}
      />
      <Route
        path={`${offeringSidePanelSellSideRouteFactory.orderBookCompliance.routePath.replace(
          offeringSidePanelSellSideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<ComplianceRoute />}
      />
      <Route
        path="*"
        element={
          <Navigate
            to={offeringSidePanelSellSideRouteFactory.orderBookIndicationActivity.getUrlPath({})}
            replace
          />
        }
      />
    </Routes>
  );
};

export default OrderBookSellSideRoute;
